import { IInputProps } from 'modules';
import { Icon } from 'components/icon';
import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_PIN_LENGTH = 6;

export const InputPinCode = (props: IInputProps): JSX.Element => {
  const requestedPinLength = props.pinLength;
  const requestedMaxPinLength = props.maxPinLength;
  const pinLength =
    typeof requestedPinLength === 'number' &&
    Number.isInteger(requestedPinLength) &&
    requestedPinLength > 0
      ? requestedPinLength
      : DEFAULT_PIN_LENGTH;
  const maxPinLength =
    typeof requestedMaxPinLength === 'number' &&
    Number.isInteger(requestedMaxPinLength) &&
    requestedMaxPinLength > 0
      ? requestedMaxPinLength
      : pinLength;
  const isSingleInput = props.pinCodeMode === 'single';
  const inputMode = props.inputMode ?? 'text';
  const normalizeInput = (raw: string) =>
    inputMode === 'numeric' ? raw.replace(/\D/g, '') : raw;
  const valueLimit = isSingleInput
    ? maxPinLength
    : Math.min(pinLength, maxPinLength);
  const [visible, setVisible] = useState(false);
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const value = normalizeInput(String(props.value ?? '')).slice(0, valueLimit);
  const preventPasswordAutofill = props.autoComplete === 'off';

  useEffect(() => {
    if (!props.autoFocus) return;
    const frame = window.requestAnimationFrame(() => refs.current[0]?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [props.autoFocus]);

  const change = (index: number, raw: string) => {
    const characters = normalizeInput(raw);
    const next = characters
      ? `${value.slice(0, index)}${characters}${value.slice(index + 1)}`
      : value.slice(0, index) + value.slice(index + 1);
    props.onTouched?.();
    props.onChange(next.slice(0, valueLimit));
    if (characters)
      refs.current[Math.min(index + characters.length, pinLength - 1)]?.focus();
  };

  const changeSingleInput = (raw: string) => {
    props.onTouched?.();
    props.onChange(normalizeInput(raw).slice(0, maxPinLength));
  };

  return (
    <div className='PinCodeInput'>
      <div
        className={`PinCodeInput__content${
          isSingleInput ? ' PinCodeInput__content--single' : ''
        }`}
      >
        <div
          className={`PinCodeInput__fields${
            isSingleInput ? ' PinCodeInput__fields--single' : ''
          }`}
        >
          {isSingleInput ? (
            <input
              ref={(element) => {
                refs.current[0] = element;
              }}
              name={props.name}
              className={`PinCodeInput__field PinCodeInput__field--single${
                preventPasswordAutofill && !visible
                  ? ' PinCodeInput__field--masked'
                  : ''
              }`}
              type={visible || preventPasswordAutofill ? 'text' : 'password'}
              inputMode={inputMode}
              pattern={props.pattern}
              autoComplete={props.autoComplete || 'one-time-code'}
              autoFocus={props.autoFocus}
              disabled={props.isDisabled}
              maxLength={maxPinLength}
              value={value}
              onChange={(event) => changeSingleInput(event.target.value)}
            />
          ) : (
            Array.from({ length: pinLength }, (_, index) => (
              <input
                key={index}
                ref={(element) => {
                  refs.current[index] = element;
                }}
                name={index === 0 ? props.name : `${props.name}-${index}`}
                className={`PinCodeInput__field${
                  preventPasswordAutofill && !visible
                    ? ' PinCodeInput__field--masked'
                    : ''
                }`}
                type={visible || preventPasswordAutofill ? 'text' : 'password'}
                inputMode={inputMode}
                pattern={props.pattern}
                autoComplete={
                  index === 0 ? props.autoComplete || 'one-time-code' : 'off'
                }
                autoFocus={props.autoFocus && index === 0}
                disabled={props.isDisabled}
                maxLength={index === 0 && props.autoComplete ? pinLength : 1}
                value={value[index] || ''}
                onChange={(event) => change(index, event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && !value[index] && index)
                    refs.current[index - 1]?.focus();
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  change(index, event.clipboardData.getData('text'));
                }}
              />
            ))
          )}
        </div>
        <button
          type='button'
          className='PinCodeInput__visibility'
          aria-label={visible ? 'Ẩn mã PIN' : 'Hiện mã PIN'}
          onClick={() => setVisible(!visible)}
        >
          {visible ? <Icon.Invisible /> : <Icon.Visible />}
        </button>
      </div>
      <div
        className='PinCodeInput__message'
        role={props.error ? 'alert' : undefined}
        aria-live='polite'
      >
        {props.error?.toString()}
      </div>
    </div>
  );
};
